/**
 * Shared source-status helpers for analytics-v2 widgets (CORE-1041).
 *
 * `degradedSourceLabels` iterates exhaustively over a widget response's
 * `sourceStatuses` map so a future 4th source is picked up automatically
 * instead of being silently unmonitored — hardcoding ga4/meta/db was the
 * silent-drift bug the CORE-959 review flagged.
 */

interface SourceStatusLike {
  ok: boolean;
  error?: string;
}

/**
 * Display labels for known source keys. Unknown keys uppercase as a
 * fallback so operators still see a readable name.
 */
const SOURCE_LABELS: Record<string, string> = {
  ga4: 'GA4',
  meta: 'Meta',
  db: 'DB',
  // Quality widget sources (CORE-961).
  bphPixel: 'Bph Pixel',
  bloophSdk: 'Blooph SDK',
  ga4Web: 'GA4 web',
  firebaseMobile: 'Firebase mobile',
};

function sourceLabel(key: string): string {
  return SOURCE_LABELS[key] ?? key.toUpperCase();
}

function isDegradedStatus(value: unknown): value is SourceStatusLike {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'ok' in value &&
    (value as SourceStatusLike).ok === false
  );
}

/**
 * Returns display labels for every source whose `.ok` is false. Skips
 * null / undefined values ("not applicable for this segment" — e.g. the
 * funnel's meta status is null for non-Meta segments; NOT the same as
 * "source is unavailable").
 *
 * Argument type is `object` (not `Record<string, ...>`) because the two
 * widget-response interfaces (AnalyticsSourceStatuses,
 * AnalyticsCampaignsSourceStatuses) don't structurally satisfy an index
 * signature in TypeScript's type system. Runtime iteration via
 * Object.entries + isDegradedStatus gives us the safety back without
 * touching the response types.
 */
export function degradedSourceLabels(statuses: object): string[] {
  return Object.entries(statuses)
    .filter(([, status]) => isDegradedStatus(status))
    .map(([key]) => sourceLabel(key));
}
